import {
  type AnnulOperation,
  type BranchChangeset,
  type ConfigurationValidationError,
  isEnumDefinitionCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey } from '@festina-life/api'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import {
  findActiveAnnulments,
  indexAnnulmentOperations,
  indexChangesetOperations,
} from '../annulOperations/helpers'
import type { ValidationErrorWithLocation } from '../errors/types'
import { userActionsForOperation } from '../helpers'
import {
  isApplicationValidationError,
  isOperationValidationError,
  type WorkspaceOperationOf,
} from '../types'
import type { EnumDefinitionWithoutLiterals } from './types'

export const useConfiguredEnumDefinitionsFromChangeset = ({
  configurationChangesets,
  errors,
  draftAnnulOperations,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[]
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
}) => {
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  return useMemo(() => {
    const operationIndexes = indexChangesetOperations(
      configurationChangesets ?? [],
    )
    const indexedAnnulments = indexAnnulmentOperations({
      changesets: configurationChangesets ?? [],
      baseBlock: rootBlock,
    })

    return (configurationChangesets ?? []).flatMap((branchChangeset) =>
      (branchChangeset.changeset?.operations ?? [])
        .filter(notEmpty)
        .filter(isEnumDefinitionCreateOperation)
        .map((definitionOperation) => {
          assertNotEmpty(definitionOperation.entity?.name)
          const actions = userActionsForOperation(
            definitionOperation,
            branchChangeset.changesetHistory,
          )

          const enumDefinitionWithoutAnnulments = {
            name: definitionOperation.entity.name,
            internalOperation: definitionOperation,
            changesetHistory: branchChangeset.changesetHistory,
            layer: new LayerKey({
              tenantId: branchChangeset.changeset?.tenantId,
              layerId: branchChangeset.changeset?.layer,
            }),
            isDraft: false,
            errors: (errors ?? []).filter(
              ({ error }) =>
                (isOperationValidationError(error) ||
                  isApplicationValidationError(error)) &&
                error.operationKey === definitionOperation.operationKey,
            ),
            actions,
            block: branchChangeset.block ?? rootBlock,
          }

          const annulOperations = findActiveAnnulments({
            operation: enumDefinitionWithoutAnnulments,
            changesetAnnulments: indexedAnnulments,
            operationIndexes,
            workspaceAnnulments: draftAnnulOperations ?? [],
          })

          return {
            ...enumDefinitionWithoutAnnulments,
            annulOperations,
          } satisfies EnumDefinitionWithoutLiterals
        }),
    )
  }, [configurationChangesets, draftAnnulOperations, errors, rootBlock])
}
